// src/pages/supplyList/useSupplyFilters.js
import { useState, useCallback, useMemo } from 'react';

/**
 * 🚀 供給履歴フィルター管理カスタムフック
 * フィルター状態とロジックを一元管理
 */
export const useSupplyFilters = () => {
  // フィルター状態
  const [clientFilter, setClientFilter] = useState(null);
  const [productFilter, setProductFilter] = useState(null); // 製品オブジェクト {id, name} を保持
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // 🔹 フィルターリセット
  const resetFilters = useCallback(() => {
    setClientFilter(null);
    setProductFilter(null);
    setStartDate('');
    setEndDate('');
  }, []);

  // 🔹 フィルターパラメータ生成（API送信用）
  const getFilterParams = useCallback(() => {
    const params = {};
    
    if (clientFilter?.id) {
      params.clientId = clientFilter.id;
    }
    
    // 製品IDのみを送信（製品名は不要）
    if (productFilter?.id) {
      params.productId = productFilter.id;
    }
    
    if (startDate) {
      params.startDate = startDate;
    }
    
    if (endDate) {
      params.endDate = endDate;
    }
    
    return params;
  }, [clientFilter, productFilter, startDate, endDate]);

  // 🔹 フィルター状態の検証
  const validateFilters = useMemo(() => {
    const errors = [];
    
    // 日付範囲の検証
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      errors.push('開始日は終了日より前の日付を選択してください');
    }
    
    // 未来日付の検証
    const today = new Date().toISOString().split('T')[0];
    if (startDate && startDate > today) {
      errors.push('開始日に未来の日付は選択できません');
    }
    if (endDate && endDate > today) {
      errors.push('終了日に未来の日付は選択できません');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }, [startDate, endDate]);

  // 🔹 フィルターが適用されているかチェック
  const hasActiveFilters = useMemo(() => {
    return !!(clientFilter || productFilter || startDate || endDate);
  }, [clientFilter, productFilter, startDate, endDate]);

  // 🔹 フィルター状態のサマリー（デバッグ用）
  const getFilterSummary = useCallback(() => {
    return {
      client: clientFilter?.name || null,
      product: productFilter?.name || null,
      dateRange: startDate && endDate 
        ? `${startDate} 〜 ${endDate}` 
        : startDate 
          ? `${startDate} 以降` 
          : endDate 
            ? `${endDate} 以前` 
            : null,
      hasFilters: hasActiveFilters
    };
  }, [clientFilter, productFilter, startDate, endDate, hasActiveFilters]);

  return {
    // 状態
    clientFilter,
    productFilter,
    startDate,
    endDate,
    
    // セッター
    setClientFilter,
    setProductFilter,
    setStartDate,
    setEndDate,
    
    // メソッド
    resetFilters,
    getFilterParams,
    getFilterSummary,
    
    // 計算されたプロパティ
    validateFilters,
    hasActiveFilters
  };
};